/*****************************************************************
 *  ZOHO BRIDGE PANEL — ONE VISIT CARD                             *
 *  File: apps/web/src/pages/bridge/VisitCard.js                   *
 *  What it does: one Zoho meeting on its way to a technician's    *
 *  Dispatch calendar: what kind of change it is (new / moved /    *
 *  changed / cancelled in Zoho), when, who, where, the office's   *
 *  flags as chips with words, why it cannot land yet, and after   *
 *  it lands, any Dispatch rule it breaks. Approve / Reject with a *
 *  reason. Honest UI: the card says only what the server said.    *
 *  Reads: nothing itself.  Writes: approve / reject, on confirm.  *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — TIMES IN THE COMPANY'S OWN ZONE                    *
 *  SECTION 2 — SMALL PIECES OF THE CARD                           *
 *  SECTION 3 — THE CARD                                           *
 *****************************************************************/

// WHY (2026-10-13, James): "Yes, build it on those defaults" - approve each for
// a one-week trial; a booking that breaks a Dispatch rule still lands, with a
// warning; a Zoho delete = cancelled in Butler, kept.

import React, { useState } from 'react';
import { BridgeIcon } from './bridgeIcons';
import {
  BRIDGE_WORDS,
  VISIT_CHANGE_NAMES,
  TECH_STATUS_NAMES,
  FLAG_NAMES,
} from './bridgeWords';
import { StatusChip, ToneChip, WhyBox, formatWhen } from './bridgeParts';
import { approveRecord, rejectRecord } from './bridgeApi';

/*****************************************************************
 *  SECTION 1 — TIMES IN THE COMPANY'S OWN ZONE                    *
 *****************************************************************/

// The zone the crews work in when Dispatch does not say (WNCIL's).
const FALLBACK_TIME_ZONE = 'America/New_York';

/* ---- formatVisitTime(startIso, endIso, timeZone) ---------------
   "Tue, Oct 13, 2:00 PM to 3:00 PM" in the company's zone.
   Out: text ('' when the start is missing).                     */
function formatVisitTime(startIso, endIso, timeZone) {
  if (!startIso) {
    return '';
  }

  const zone = timeZone || FALLBACK_TIME_ZONE;
  const dayAndStart = new Date(startIso).toLocaleString(undefined, {
    timeZone: zone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  if (!endIso) {
    return dayAndStart;
  }

  const endOnly = new Date(endIso).toLocaleString(undefined, {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return dayAndStart + ' to ' + endOnly;
}

/*****************************************************************
 *  SECTION 2 — SMALL PIECES OF THE CARD                           *
 *****************************************************************/

// The chip each change wears (word + icon + colour, never colour alone).
const CHANGE_LOOKS = {
  new: { tone: 'info', icon: 'calendar' },
  moved: { tone: 'warn', icon: 'clock' },
  changed: { tone: 'info', icon: 'history' },
  cancel: { tone: 'bad', icon: 'cross' },
};

/* ---- techWords(tech, fallbackName) -----------------------------
   "Taylor Sorrells" when confirmed, otherwise the Zoho name plus
   where the match is.  Out: text.                               */
function techWords(tech, fallbackName) {
  if (tech && tech.techStatus === 'confirmed' && tech.employeeName) {
    return tech.employeeName;
  }

  const status = tech ? tech.techStatus : 'none';
  let where = BRIDGE_WORDS.techNotMatched;

  if (status === 'proposed') {
    where = BRIDGE_WORDS.techWaitingForYou;
  }

  if (status === 'unmatched') {
    where = BRIDGE_WORDS.techSaysNot;
  }

  return (fallbackName || '?') + ' (' + where + ')';
}

/* ---- FactRow({ label, value }) -- one labelled fact, or nothing ---- */
function FactRow({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="zb-fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/* ---- FlagChips({ flags }) -- the office's flags as word chips ---- */
function FlagChips({ flags }) {
  const list = Array.isArray(flags) ? flags : [];

  if (!list.length) {
    return null;
  }

  return (
    <div className="zb-row zb-row-tight">
      {list.map((oneFlag) => (
        <ToneChip key={oneFlag} tone="info" icon="flag" word={FLAG_NAMES[oneFlag] || oneFlag} />
      ))}
    </div>
  );
}

/* ---- WarningList({ warnings }) -- Dispatch's answer, after it landed ---- */
function WarningList({ warnings }) {
  const list = Array.isArray(warnings) ? warnings : [];

  if (!list.length) {
    return null;
  }

  const broken = list.filter((oneWarning) => oneWarning.level === 'warning');
  const worthKnowing = list.filter((oneWarning) => oneWarning.level !== 'warning');

  return (
    <div className="zb-block">
      {broken.length ? (
        <div className="zb-notice zb-notice-warn">
          <BridgeIcon name="alert" />
          <div>
            <strong>{BRIDGE_WORDS.rulesBroken}</strong>
            <ul className="zb-changes">
              {broken.map((oneWarning, index) => <li key={'w' + index}>{oneWarning.words}</li>)}
            </ul>
          </div>
        </div>
      ) : null}
      {worthKnowing.length ? (
        <div className="zb-notice zb-notice-info">
          <BridgeIcon name="list" />
          <div>
            <strong>{BRIDGE_WORDS.goodToKnow}</strong>
            <ul className="zb-changes">
              {worthKnowing.map((oneWarning, index) => <li key={'i' + index}>{oneWarning.words}</li>)}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/*****************************************************************
 *  SECTION 3 — THE CARD                                           *
 *****************************************************************/

/* ---- VisitCard({ record, timeZone, isSelected, onToggleSelect, onDecided })
   One visit. onDecided() reloads the tab and the page counts.   */
export default function VisitCard({ record, timeZone, isSelected, onToggleSelect, onDecided }) {
  const [openBox, setOpenBox] = useState('');          // '', 'approve' or 'reject'
  const [isBusy, setIsBusy] = useState(false);          // a call is on its way
  const [answerText, setAnswerText] = useState('');     // what the server said
  const [answerTone, setAnswerTone] = useState('info'); // how to colour it

  const data = record.data || {};
  const preview = record.diff || {};
  const change = data.change === 'cancel' ? 'cancel' : (preview.change || 'new');
  const changeLook = CHANGE_LOOKS[change] || CHANGE_LOOKS.new;
  const canDecide = record.status === 'proposed' || record.status === 'failed';
  const isWaiting = record.status === 'proposed';
  const checkboxId = 'zb-select-' + record.id;
  const titleId = 'zb-title-' + record.id;
  const titleText = data.title || data.dealName || record.zohoId;

  const helperWords = (record.helperTechs || []).map((helper) => techWords(helper, helper.name)).join(', ');
  const previousTime = preview.previous ? formatVisitTime(preview.previous.startsAt, preview.previous.endsAt, timeZone) : '';
  const problems = isWaiting && Array.isArray(preview.problems) ? preview.problems : [];
  const appliedDiff = record.status === 'applied' ? preview : {};

  /* ---- sendDecision(whyText) -- approve or reject, then report ---- */
  async function sendDecision(whyText) {
    setIsBusy(true);
    setAnswerText('');

    const answer = openBox === 'approve'
      ? await approveRecord(record.id, whyText, false)
      : await rejectRecord(record.id, whyText);

    setIsBusy(false);

    if (!answer.ok) {
      setAnswerTone('bad');
      setAnswerText(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    if (answer.data && answer.data.outcome === 'failed') {
      setAnswerTone('bad');
      setAnswerText(BRIDGE_WORDS.failedBecause + ' ' + answer.data.message);
    }

    setOpenBox('');
    onDecided();
  }

  return (
    <article className="zb-card" aria-labelledby={titleId}>
      <header className="zb-card-head">
        {canDecide ? (
          <label className="zb-check zb-check-only" htmlFor={checkboxId}>
            <input id={checkboxId} type="checkbox" checked={isSelected} onChange={() => onToggleSelect(record.id)} />
            <span className="zb-visually-hidden">Select {titleText}</span>
          </label>
        ) : null}
        <div className="zb-card-titles">
          <h3 className="zb-card-title" id={titleId}>{titleText}</h3>
          <div className="zb-row zb-row-tight">
            <ToneChip tone={changeLook.tone} icon={changeLook.icon} word={VISIT_CHANGE_NAMES[change]} />
            {data.isPickup ? <ToneChip tone="neutral" icon="pin" word={BRIDGE_WORDS.visitPickup} /> : null}
            <StatusChip status={record.status} />
          </div>
        </div>
      </header>

      <div className="zb-card-body">
        <FlagChips flags={data.flags} />

        <dl className="zb-facts">
          {change !== 'cancel' ? <FactRow label={BRIDGE_WORDS.visitWhen} value={formatVisitTime(data.startsAt, data.endsAt, timeZone)} /> : null}
          {previousTime && change === 'moved' ? <FactRow label={BRIDGE_WORDS.visitWas} value={previousTime} /> : null}
          {change !== 'cancel' ? <FactRow label={BRIDGE_WORDS.visitTech} value={techWords(record.hostTech, data.hostName)} /> : null}
          <FactRow label={BRIDGE_WORDS.visitHelpers} value={helperWords} />
          <FactRow label={BRIDGE_WORDS.visitAddress} value={data.address} />
          <FactRow label={BRIDGE_WORDS.visitCustomer} value={data.contactName} />
          <FactRow label={BRIDGE_WORDS.visitService} value={data.serviceCode} />
          <FactRow label={BRIDGE_WORDS.visitJob} value={data.dealName} />
          <FactRow label={BRIDGE_WORDS.visitNotes} value={data.description} />
          <FactRow label={BRIDGE_WORDS.visitBookedBy} value={data.bookedBy} />
          {change === 'cancel' ? <FactRow label={BRIDGE_WORDS.visitDeletedBy} value={[data.deletedBy, formatWhen(data.deletedAt)].filter(Boolean).join(', ')} /> : null}
        </dl>

        {problems.length ? (
          <div className="zb-notice zb-notice-warn">
            <BridgeIcon name="alert" />
            <div>
              <strong>{BRIDGE_WORDS.cannotLandYet}</strong>
              <ul className="zb-changes">
                {problems.map((oneProblem, index) => <li key={'p' + index}>{oneProblem}</li>)}
              </ul>
            </div>
          </div>
        ) : null}

        <WarningList warnings={appliedDiff.warnings} />

        {appliedDiff.helpersLeftOff && appliedDiff.helpersLeftOff.length ? (
          <p className="zb-small">{BRIDGE_WORDS.leftOff}: {appliedDiff.helpersLeftOff.join(', ')}</p>
        ) : null}

        {record.status === 'failed' && record.failureReason ? (
          <div className="zb-notice zb-notice-bad">
            <BridgeIcon name="alert" />
            <span>{BRIDGE_WORDS.failedBecause} {record.failureReason}</span>
          </div>
        ) : null}

        {appliedDiff.consoleUrl ? (
          <p className="zb-small">
            <a href={appliedDiff.consoleUrl} target="_blank" rel="noopener noreferrer">{BRIDGE_WORDS.openInDispatch}</a>
          </p>
        ) : null}

        <p className="zb-small">
          {BRIDGE_WORDS.receivedAt} {formatWhen(record.receivedAt)}
          {record.decidedAt ? ' · ' + BRIDGE_WORDS.decidedBy + ' ' + formatWhen(record.decidedAt) : ''}
          {record.decisionWhy ? ' — "' + record.decisionWhy + '"' : ''}
          {record.hostTech && record.hostTech.techStatus ? ' · ' + BRIDGE_WORDS.visitTech + ': ' + TECH_STATUS_NAMES[record.hostTech.techStatus] : ''}
        </p>
      </div>

      {answerText ? (
        <div className={'zb-notice zb-notice-' + answerTone} role="status">
          <span>{answerText}</span>
        </div>
      ) : null}

      {canDecide && openBox === '' ? (
        <div className="zb-row">
          <button type="button" className="zb-button zb-button-primary" onClick={() => setOpenBox('approve')}>
            <BridgeIcon name="check" />
            <span>{BRIDGE_WORDS.approve}</span>
          </button>
          <button type="button" className="zb-button zb-button-plain" onClick={() => setOpenBox('reject')}>
            <BridgeIcon name="cross" />
            <span>{BRIDGE_WORDS.reject}</span>
          </button>
        </div>
      ) : null}

      {canDecide && openBox !== '' ? (
        <WhyBox
          confirmWord={openBox === 'approve' ? (change === 'cancel' ? BRIDGE_WORDS.confirmCancel : BRIDGE_WORDS.confirmVisit) : BRIDGE_WORDS.confirmReject}
          tone={openBox === 'approve' ? 'primary' : 'danger'}
          busy={isBusy}
          onConfirm={sendDecision}
          onCancel={() => setOpenBox('')}
        />
      ) : null}
    </article>
  );
}
